import { Controller } from '@hotwired/stimulus';
import { polygonPathToWkt } from './mechanic_coverage_polygon_wkt';

const MAPS_CALLBACK = 'onMechanicCoverageMapsReady';

function openRing(ring) {
  if (ring.length < 2) return ring;

  const [firstLongitude, firstLatitude] = ring[0];
  const [lastLongitude, lastLatitude] = ring[ring.length - 1];
  if (firstLongitude === lastLongitude && firstLatitude === lastLatitude) {
    return ring.slice(0, -1);
  }

  return ring;
}

const INITIAL_HINT = 'Draw new area clears the shape. Click the map once for each corner, then choose Finish shape.';
const SHAPE_PADDING_PX = 32;

export default class extends Controller {
  static targets = ['canvas', 'polygonWkt', 'hint', 'finish'];

  static values = {
    latitude: Number,
    longitude: Number,
    mapsApiKey: String,
    polygon: Object,
  };

  connect() {
    this.coveragePolygon = null;
    this.sketchPoints = [];
    this.sketchMarkers = [];
    this.sketchLine = null;
    this.sketchListener = null;
    this.#loadDrawingMaps().then(() => {
      this.#showMechanicBase();
    });
  }

  save(event) {
    if (!this.coveragePolygon) {
      event.preventDefault();
      return;
    }

    if (!this.hasPolygonWktTarget) return;

    this.polygonWktTarget.value = polygonPathToWkt(this.coveragePolygon.getPath());
  }

  clear(event) {
    event.preventDefault();
    if (this.coveragePolygon) {
      this.coveragePolygon.setMap(null);
      this.coveragePolygon = null;
    }
    this.#resetSketch();
    this.#startSketch();
  }

  finish(event) {
    event.preventDefault();
    if (this.sketchPoints.length < 3) return;

    const path = this.sketchPoints.slice();
    this.#resetSketch();
    this.coveragePolygon = new google.maps.Polygon({
      paths: path,
      map: this.map,
      editable: true,
      draggable: false,
    });
    this.#setHint(INITIAL_HINT);
  }

  #loadDrawingMaps() {
    if (window.google && window.google.maps && window.google.maps.Map) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      window[MAPS_CALLBACK] = () => {
        delete window[MAPS_CALLBACK];
        resolve();
      };

      const script = document.createElement('script');
      const params = new URLSearchParams({
        key: this.mapsApiKeyValue,
        language: 'en',
        libraries: 'drawing',
        callback: MAPS_CALLBACK,
      });
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.onerror = () => {
        delete window[MAPS_CALLBACK];
        reject(new Error('Google Maps failed to load'));
      };
      document.head.appendChild(script);
    });
  }

  #showMechanicBase() {
    const center = new google.maps.LatLng(this.latitudeValue, this.longitudeValue);
    const map = new google.maps.Map(this.canvasTarget, {
      center,
      zoom: 11,
      scrollwheel: false,
      streetViewControl: false,
    });

    this.map = map;

    new google.maps.Marker({
      position: center,
      map,
    });

    this.#showStoredPolygon(map);
  }

  #showStoredPolygon(map) {
    if (!this.hasPolygonValue) return;

    const rings = this.polygonValue.coordinates;
    if (!Array.isArray(rings)) return;

    const paths = rings.map((ring) => openRing(ring).map(([longitude, latitude]) => (
      new google.maps.LatLng(latitude, longitude)
    )));

    this.coveragePolygon = new google.maps.Polygon({
      paths,
      map,
      editable: true,
      draggable: false,
    });
    this.#fitMapToShape(map, paths.flat());
  }

  #fitMapToShape(map, latLngs) {
    const bounds = new google.maps.LatLngBounds();
    latLngs.forEach((latLng) => bounds.extend(latLng));
    if (bounds.isEmpty()) return;

    map.fitBounds(bounds, SHAPE_PADDING_PX);
  }

  #startSketch() {
    this.sketchListener = this.map.addListener('click', (event) => {
      this.#addSketchCorner(event.latLng);
    });
    this.canvasTarget.classList.add('is-drawing');
    if (this.hasFinishTarget) {
      this.finishTarget.hidden = false;
      this.finishTarget.disabled = true;
    }
    this.#setHint('Click the map to place the first corner.');
  }

  #addSketchCorner(latLng) {
    this.sketchPoints.push(latLng);
    this.sketchMarkers.push(new google.maps.Marker({
      position: latLng,
      map: this.map,
      label: {
        text: String(this.sketchPoints.length),
        color: '#ffffff',
        fontWeight: 'bold',
      },
    }));
    this.#updateSketchLine();

    const count = this.sketchPoints.length;
    if (this.hasFinishTarget) this.finishTarget.disabled = count < 3;

    const noun = count === 1 ? 'corner' : 'corners';
    this.#setHint(
      count < 3
        ? `${count} ${noun} placed. Click the map to add the next corner.`
        : `${count} corners placed. Add another, or choose Finish shape.`,
    );
  }

  #updateSketchLine() {
    if (!this.sketchLine) {
      this.sketchLine = new google.maps.Polyline({
        map: this.map,
        path: this.sketchPoints,
        strokeWeight: 2,
      });
      return;
    }

    this.sketchLine.setPath(this.sketchPoints);
  }

  #resetSketch() {
    this.sketchListener?.remove();
    this.sketchListener = null;
    this.sketchMarkers.forEach((marker) => marker.setMap(null));
    this.sketchMarkers = [];
    this.sketchLine?.setMap(null);
    this.sketchLine = null;
    this.sketchPoints = [];
    this.canvasTarget.classList.remove('is-drawing');
    if (this.hasFinishTarget) {
      this.finishTarget.hidden = true;
      this.finishTarget.disabled = true;
    }
  }

  #setHint(text) {
    if (this.hasHintTarget) this.hintTarget.textContent = text;
  }
}
